import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useActiveTicketsWithLoaded } from "../hooks/useActiveTickets";
import { usePinnedTickets } from "../hooks/usePinnedTickets";
import { reconcilePins, updatePinSnapshot } from "../lib/pinnedTickets";
import PinnedTicketBubble from "./PinnedTicketBubble";

/**
 * Renders every pinned-ticket floating bubble (one per active pinned
 * ticket, max 2 per spec). Mounted ONCE at the app root so the bubbles
 * stay alive while the visitor navigates between routes (Home, Search,
 * CommerceDetail, MyTicket, etc.). Hidden only on routes where they
 * would be inappropriate (fullscreen TV, merchant/admin dashboards).
 *
 * RESILIENCE : each pin entry persists a SNAPSHOT of the ticket payload
 * so the bubble renders IMMEDIATELY when the visitor pins a fresh
 * ticket — without waiting for the next active-tickets poll cycle
 * (which can take up to 3 s and used to make the bubble flash invisible).
 * The snapshot is refreshed on every successful poll so the bubble stays
 * in sync with live data (status, position, code).
 */
const HIDDEN_PREFIXES = ["/display/", "/merchant/", "/admin"];
const HIDDEN_EXACT = ["/tv"];

export default function PinnedTicketsLayer() {
  const { role, user } = useAuth();
  const { tickets, loaded } = useActiveTicketsWithLoaded(3000);
  const pinned = usePinnedTickets();
  const loc = useLocation();

  // Drop pinned entries that no longer correspond to an active ticket.
  // This keeps localStorage clean after a ticket is cancelled / served.
  // CRITICAL : wait for the first API response (`loaded`) before deciding
  // a ticket is "gone" — otherwise the initial empty list during a route
  // change would wipe every pin out.
  useEffect(() => {
    if (!loaded) return;
    reconcilePins((tickets || []).map((t) => t.id));
  }, [tickets, loaded]);

  // Keep the persisted snapshot fresh for each pinned ticket. This is
  // what guarantees the bubble keeps rendering the correct code /
  // position / status even after the user closes the browser tab and
  // reopens it on a route where the active-tickets list is still warming
  // up.
  useEffect(() => {
    if (!loaded) return;
    for (const t of tickets || []) {
      if (pinned[t.id]) updatePinSnapshot(t.id, t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tickets, loaded]);

  // Layer is irrelevant for establishment / admin / TV routes.
  if (role === "commerce") return null;
  if (user?.isAdmin) return null;
  const path = loc.pathname || "/";
  if (HIDDEN_EXACT.includes(path)) return null;
  if (HIDDEN_PREFIXES.some((p) => path.startsWith(p))) return null;

  // Build the list of pinned tickets, using LIVE data when available and
  // falling back to the persisted snapshot when not yet hydrated.
  // Preserves the pin order so a freshly added pin slides above the
  // previous one (stackIndex grows with `pinnedAt`).
  const byId = new Map((tickets || []).map((t) => [t.id, t]));
  const visible = Object.entries(pinned)
    .map(([id, pin]) => {
      const ticket = byId.get(id) || pin.snapshot || null;
      return ticket ? { id, ticket, pin } : null;
    })
    .filter(Boolean)
    .sort((a, b) => (a.pin.pinnedAt || 0) - (b.pin.pinnedAt || 0))
    .map(({ ticket, pin }, idx) => ({ ticket, pin, stackIndex: idx }));

  if (visible.length === 0) return null;

  return (
    <div aria-live="polite" data-testid="pinned-tickets-layer">
      {visible.map(({ ticket, pin, stackIndex }) => (
        <PinnedTicketBubble
          key={ticket.id}
          ticket={ticket}
          pin={pin}
          stackIndex={stackIndex}
        />
      ))}
    </div>
  );
}
